import { DateFormatter, getLocalTimeZone, parseDate, startOfMonth, today } from '@internationalized/date';
import { Modal } from '@numa-group/ui.modal';
import { differenceInDays } from 'date-fns';
import { Fragment, useMemo, useState } from 'react';
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  RangeCalendar,
} from 'react-aria-components';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import styles from './search-dates.module.css';
import { useBottomNavigation } from '../../../../bottom-navigation.context';
import { SearchProps } from '../../search';
import { SearchSchema } from '../../search.schema';

type SearchDatesProps = {
  onSelect: VoidFunction;
  cities: SearchProps['cities'];
};
export const SearchDates = ({ onSelect, cities }: SearchDatesProps) => {
  const timeZone = getLocalTimeZone();
  const { formatMessage } = useIntl();
  const { locale, content } = useBottomNavigation();
  const form = useFormContext<SearchSchema>();
  const [monthsShown, setMonthsShown] = useState(3);
  const { arrival, departure } = form.watch('dates') || {};
  const location = form.watch('location');
  const cityName = cities.find(({ slug }) => slug === location)?.cityName;

  const { dateRange, numberOfNights } = useMemo(() => {
    if (!arrival || !departure) {
      return {
        dateRange: '',
        numberOfNights: undefined,
      };
    }

    const formatter = new DateFormatter(locale);

    const dateRange = formatter.formatRange(parseDate(arrival).toDate(timeZone), parseDate(departure).toDate(timeZone));
    const numberOfNights = differenceInDays(parseDate(departure).toDate(timeZone), parseDate(arrival).toDate(timeZone));

    return { dateRange, numberOfNights };
  }, [arrival, departure, locale, timeZone]);

  const getMonthHeading = (index: number) => {
    const currentMonth = startOfMonth(today(timeZone));
    const month = currentMonth.add({
      months: index,
    });
    const monthName = new DateFormatter(locale, { month: 'long' }).format(month.toDate(timeZone));
    const year = month.year;

    return `${monthName} ${year}`;
  };

  const nightsText =
    numberOfNights &&
    `x ${formatMessage(
      {
        id: 'nights',
        defaultMessage: content.nights,
      },
      {
        numberOfNights,
      },
    )}`;

  const subTitle = [cityName, dateRange, nightsText].filter(Boolean).join(', ');

  return (
    <>
      <Modal.Body>
        <Modal.Content>
          <Controller
            control={form.control}
            name="dates"
            render={({ field }) => {
              const calendarValue = field.value
                ? {
                    start: parseDate(field.value.arrival),
                    end: parseDate(field.value.departure),
                  }
                : null;

              return (
                <RangeCalendar
                  className={styles.calendar}
                  minValue={today(timeZone)}
                  visibleDuration={{
                    months: monthsShown,
                  }}
                  pageBehavior="single"
                  value={calendarValue}
                  onChange={(value) => {
                    field.onChange({
                      arrival: value?.start.toString(),
                      departure: value?.end.toString(),
                    });
                  }}
                >
                  <header className={styles.calendarDaysHeader}>
                    <span>{subTitle}</span>

                    <CalendarGrid weekdayStyle="short">
                      <CalendarGridHeader>
                        {(day) => <CalendarHeaderCell className={styles.calendarDay}>{day}</CalendarHeaderCell>}
                      </CalendarGridHeader>
                      <CalendarGridBody>{() => <></>}</CalendarGridBody>
                    </CalendarGrid>
                  </header>

                  <div className={styles.calendarMonths}>
                    {[...new Array(monthsShown)].map((_, index) => (
                      <Fragment key={index}>
                        <header className={styles.monthHeading}>{getMonthHeading(index)}</header>

                        <CalendarGrid
                          offset={{
                            months: index,
                          }}
                        >
                          <CalendarGridBody>
                            {(date) => <CalendarCell date={date} className={styles.calendarCell} />}
                          </CalendarGridBody>
                        </CalendarGrid>
                      </Fragment>
                    ))}
                  </div>
                </RangeCalendar>
              );
            }}
          />
          <button type="button" onClick={() => setMonthsShown(monthsShown + 3)}>
            {content.loadMore}
          </button>
        </Modal.Content>
      </Modal.Body>

      <Modal.Footer>
        <Modal.FooterAction type="button" onClick={onSelect}>
          {content.confirmCta}
        </Modal.FooterAction>
      </Modal.Footer>
    </>
  );
};
